import { useState } from 'react';
import { getFormattedDateUTC } from 'utils/getFormattedDate';
import { Box, Stack, Typography, Divider, Chip, Collapse, IconButton, LinearProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  FileDoneOutlined,
  DownOutlined,
  RightOutlined,
  BankOutlined,
  SearchOutlined,
  CreditCardOutlined,
  RiseOutlined,
  WarningFilled,
  DollarOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import VisibilityBadge from './UserViewBadge';
import { ReportData, ReportTradeline } from '../types';

interface Props {
  data: ReportData;
}

const MONO_FONT = 'SF Mono, Menlo, Consolas, monospace';

const formatMoney = (value: number | undefined): string => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  if (value === 0) return '$0';
  return `$${Math.round(value).toLocaleString()}`;
};

const categoryColor = (category: string | undefined, theme: any): string => {
  if (!category) return theme.palette.grey[500];
  const c = category.toLowerCase();
  if (c.includes('excell') || c.includes('except')) return theme.palette.success.dark;
  if (c.includes('good') || c.includes('prime')) return theme.palette.success.main;
  if (c.includes('fair') || c.includes('near')) return theme.palette.warning.main;
  if (c.includes('poor') || c.includes('deep') || c.includes('bad') || c.includes('sub')) return theme.palette.error.main;
  return theme.palette.info.main;
};

const Metric = ({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: string; sub?: string; color?: string }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      p: 1.25,
      borderRadius: 1.25,
      bgcolor: alpha(theme.palette.background.paper, 0.65),
      border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
    }}>
      <Stack direction="row" spacing={0.625} alignItems="center" sx={{ mb: 0.5 }}>
        <Box sx={{ color: color ?? alpha(theme.palette.text.primary, 0.65), fontSize: 11, display: 'flex' }}>{icon}</Box>
        <Typography sx={{
          fontSize: '0.78rem',
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: alpha(theme.palette.text.primary, 0.65),
        }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{
        fontSize: '1rem',
        fontWeight: 700,
        color: color ?? theme.palette.text.primary,
        fontFamily: MONO_FONT,
        lineHeight: 1.1,
      }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{
          fontSize: '0.78rem',
          color: alpha(theme.palette.text.primary, 0.65),
          mt: 0.25,
        }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
};

const Section = ({
  title, count, defaultOpen = false, children,
}: { title: string; count?: number; defaultOpen?: boolean; children: React.ReactNode }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mb: 0.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setOpen((o) => !o)}
        sx={{
          cursor: 'pointer',
          py: 0.75,
          borderRadius: 1,
          '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) },
        }}
      >
        <IconButton size="small" sx={{ width: 20, height: 20, p: 0, flexShrink: 0 }}>
          {open ? (
            <DownOutlined style={{ fontSize: 10, color: theme.palette.text.secondary }} />
          ) : (
            <RightOutlined style={{ fontSize: 10, color: theme.palette.text.secondary }} />
          )}
        </IconButton>
        <Typography sx={{
          fontSize: '0.75rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 1.1,
          color: theme.palette.text.secondary,
        }}>
          {title}
        </Typography>
        {count !== undefined && (
          <Box sx={{
            px: 0.875, py: 0.125,
            borderRadius: 0.75,
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            color: theme.palette.text.secondary,
            fontSize: '0.78rem',
            fontWeight: 700,
            fontFamily: MONO_FONT,
          }}>
            {count}
          </Box>
        )}
      </Stack>
      <Collapse in={open}>
        <Box sx={{ pl: 3, pr: 0.5, pb: 0.75 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

const TradelineRow = ({ tl }: { tl: ReportTradeline }) => {
  const theme = useTheme();
  const accent = tl.derogatory ? theme.palette.error.main : theme.palette.text.primary;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.625 }}>
      <BankOutlined style={{ fontSize: 12, color: tl.derogatory ? theme.palette.error.main : theme.palette.primary.main, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: accent,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {tl.subscriberName}
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ mt: 0.125 }}>
          {tl.accountTypeDescription && (
            <Typography sx={{ fontSize: '0.78rem', color: alpha(theme.palette.text.primary, 0.65), textTransform: 'capitalize' }}>
              {tl.accountTypeDescription}
            </Typography>
          )}
          {tl.statusDescription && (
            <Chip
              label={tl.statusDescription}
              size="small"
              sx={{
                height: 16, fontSize: '0.72rem', fontWeight: 600,
                bgcolor: alpha(tl.derogatory ? theme.palette.error.main : theme.palette.success.main, 0.15),
                color: tl.derogatory ? theme.palette.error.main : theme.palette.success.main,
                '& .MuiChip-label': { px: 0.625 },
              }}
            />
          )}
        </Stack>
      </Box>
      <Stack spacing={0} alignItems="flex-end" sx={{ flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, fontFamily: MONO_FONT, color: theme.palette.text.primary }}>
          {formatMoney(tl.balance)}
        </Typography>
        {tl.creditLimit !== undefined && tl.revolving && (
          <Typography sx={{ fontSize: '0.72rem', color: alpha(theme.palette.text.primary, 0.65), fontFamily: MONO_FONT }}>
            of {formatMoney(tl.creditLimit)}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const ReportCard = ({ data }: Props) => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const score = data.score;
  const metrics = data.metrics;
  const tradelines = data.tradelines ?? [];
  const inquiries = data.inquiries ?? [];
  const sourceLabel = data.source === 'clear' ? 'CLEAR Report' : 'Experian Credit Profile';
  const scoreColor = categoryColor(score?.category, theme);
  const utilization = metrics?.revolvingUtilizationPct;
  const utilizationColor =
    utilization === undefined ? alpha(theme.palette.text.primary, 0.65)
      : utilization >= 70 ? theme.palette.error.main
      : utilization >= 30 ? theme.palette.warning.main
      : theme.palette.success.main;

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        bgcolor: alpha(accent, 0.04),
        border: `1px solid ${alpha(accent, 0.22)}`,
        borderLeft: `3px solid ${accent}`,
        mb: 2,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.75,
          py: 1.25,
          background: `linear-gradient(90deg, ${alpha(accent, 0.1)} 0%, ${alpha(accent, 0.02)} 100%)`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{
            width: 28, height: 28, borderRadius: 1.25,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${accent}, ${theme.palette.primary.dark})`,
            boxShadow: `0 2px 6px ${alpha(accent, 0.4)}`,
          }}>
            <FileDoneOutlined style={{ fontSize: 14, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{
              fontWeight: 700, fontSize: '0.8125rem',
              color: theme.palette.text.primary, lineHeight: 1.2,
            }}>
              {sourceLabel}
            </Typography>
            <Typography sx={{
              fontSize: '0.78rem', color: alpha(theme.palette.text.primary, 0.65),
              fontFamily: MONO_FONT, letterSpacing: 0.3,
            }}>
              {getFormattedDateUTC(data.reportPulledAt)}
            </Typography>
          </Box>
          {data.stale && (
            <Chip
              label={data.staleDays !== undefined ? `${data.staleDays}d stale` : 'stale'}
              size="small"
              sx={{
                height: 20, fontSize: '0.78rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                bgcolor: alpha(theme.palette.warning.main, 0.18),
                color: theme.palette.warning.main,
                '& .MuiChip-label': { px: 0.875 },
              }}
            />
          )}
        </Stack>
        <VisibilityBadge mode="hidden_from_ai" />
      </Stack>

      <Divider sx={{ borderColor: alpha(accent, 0.15) }} />

      <Box sx={{ p: 1.75 }}>
        {score && (
          <Box sx={{
            p: 1.75, mb: 1.75,
            borderRadius: 1.5,
            background: `linear-gradient(135deg, ${alpha(scoreColor, 0.12)}, ${alpha(scoreColor, 0.04)})`,
            border: `1px solid ${alpha(scoreColor, 0.3)}`,
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
              <Stack direction="row" alignItems="baseline" spacing={1.25}>
                <Typography sx={{
                  fontSize: '2.75rem', fontWeight: 800, lineHeight: 1,
                  color: scoreColor, fontFamily: MONO_FONT, letterSpacing: -1.5,
                }}>
                  {score.value || '—'}
                </Typography>
                <Stack spacing={0.125}>
                  {score.category && (
                    <Typography sx={{
                      fontSize: '0.875rem', fontWeight: 700,
                      color: scoreColor, textTransform: 'capitalize',
                    }}>
                      {score.category}
                    </Typography>
                  )}
                  <Typography sx={{
                    fontSize: '0.78rem', fontWeight: 600,
                    color: alpha(theme.palette.text.primary, 0.65),
                    textTransform: 'uppercase', letterSpacing: 0.8,
                  }}>
                    {score.modelName || 'Credit Score'}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            {score.factors && score.factors.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1.5, pt: 1.25, borderTop: `1px dashed ${alpha(scoreColor, 0.25)}` }}>
                <Typography sx={{
                  fontSize: '0.78rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 0.8,
                  color: alpha(theme.palette.text.primary, 0.65), mb: 0.25,
                }}>
                  Key Factors
                </Typography>
                {score.factors.slice(0, 3).map((f) => (
                  <Stack key={f.code} direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{
                      fontFamily: MONO_FONT, fontSize: '0.72rem', fontWeight: 700,
                      color: scoreColor, bgcolor: alpha(scoreColor, 0.15),
                      px: 0.625, py: 0.125, borderRadius: 0.5, minWidth: 24, textAlign: 'center',
                    }}>
                      {f.code}
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.primary, flex: 1 }}>
                      {f.description}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {metrics && (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1,
            mb: 1.5,
          }}>
            <Metric
              icon={<CreditCardOutlined />}
              label="Revolving"
              value={formatMoney(metrics.revolvingBalance)}
              sub={utilization !== undefined ? `${utilization}% utilized` : undefined}
              color={utilization !== undefined && utilization >= 30 ? utilizationColor : undefined}
            />
            <Metric
              icon={<DollarOutlined />}
              label="Installment"
              value={formatMoney(metrics.installmentBalance + metrics.realEstateBalance)}
              sub={metrics.realEstateBalance > 0 ? `incl. real estate ${formatMoney(metrics.realEstateBalance)}` : undefined}
            />
            <Metric
              icon={<LineChartOutlined />}
              label="Monthly Payment"
              value={formatMoney(metrics.totalMonthlyPayment)}
            />
            <Metric
              icon={<WarningFilled />}
              label="Past Due"
              value={formatMoney(metrics.pastDueAmount)}
              color={metrics.pastDueAmount > 0 ? theme.palette.error.main : undefined}
            />
            <Metric
              icon={<BankOutlined />}
              label="Accounts"
              value={`${metrics.openAccounts}/${metrics.totalAccounts}`}
              sub={metrics.delinquentAccounts > 0 ? `${metrics.delinquentAccounts} delinquent` : 'open/total'}
              color={metrics.delinquentAccounts > 0 ? theme.palette.warning.main : undefined}
            />
            <Metric
              icon={<SearchOutlined />}
              label="Inquiries"
              value={String(metrics.inquiriesLast6Mo)}
              sub={`last 6mo · ${metrics.totalInquiries} total`}
            />
          </Box>
        )}

        {utilization !== undefined && (
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: alpha(theme.palette.text.primary, 0.65), textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Revolving Utilization
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: utilizationColor, fontFamily: MONO_FONT }}>
                {utilization}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={utilization}
              sx={{
                height: 6,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.text.primary, 0.08),
                '& .MuiLinearProgress-bar': {
                  bgcolor: utilizationColor,
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        )}

        {metrics?.publicRecords !== undefined && metrics.publicRecords > 0 && (
          <Box sx={{
            p: 1.25, mb: 1.25,
            borderRadius: 1.25,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
          }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <WarningFilled style={{ fontSize: 13, color: theme.palette.error.main }} />
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.palette.error.main }}>
                {metrics.publicRecords} public record{metrics.publicRecords > 1 ? 's' : ''} on file
              </Typography>
            </Stack>
          </Box>
        )}

        {tradelines.length > 0 && (
          <Section title="Tradelines" count={tradelines.length} defaultOpen={tradelines.length <= 5}>
            <Stack divider={<Divider sx={{ borderStyle: 'dashed', borderColor: alpha(theme.palette.divider, 0.45) }} />}>
              {tradelines.slice(0, 10).map((tl, i) => (
                <TradelineRow key={i} tl={tl} />
              ))}
              {tradelines.length > 10 && (
                <Typography sx={{ fontSize: '0.75rem', color: alpha(theme.palette.text.primary, 0.65), textAlign: 'center', pt: 1, fontStyle: 'italic' }}>
                  +{tradelines.length - 10} more
                </Typography>
              )}
            </Stack>
          </Section>
        )}

        {inquiries.length > 0 && (
          <Section title="Inquiries" count={inquiries.length}>
            <Stack spacing={0.5}>
              {inquiries.slice(0, 8).map((inq, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.375 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <RiseOutlined style={{ fontSize: 11, color: alpha(theme.palette.text.primary, 0.65), flexShrink: 0 }} />
                    <Typography sx={{
                      fontSize: '0.8125rem', color: theme.palette.text.primary,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {inq.subscriberName}
                    </Typography>
                    {inq.kobDescription && (
                      <Typography sx={{ fontSize: '0.78rem', color: alpha(theme.palette.text.primary, 0.65), flexShrink: 0 }}>
                        · {inq.kobDescription}
                      </Typography>
                    )}
                  </Stack>
                  {inq.date && (
                    <Typography sx={{
                      color: alpha(theme.palette.text.primary, 0.65), fontSize: '0.75rem',
                      fontFamily: MONO_FONT, flexShrink: 0,
                    }}>
                      {inq.date}
                    </Typography>
                  )}
                </Stack>
              ))}
              {inquiries.length > 8 && (
                <Typography sx={{ fontSize: '0.75rem', color: alpha(theme.palette.text.primary, 0.65), textAlign: 'center', pt: 0.5, fontStyle: 'italic' }}>
                  +{inquiries.length - 8} more
                </Typography>
              )}
            </Stack>
          </Section>
        )}

        {data.messages && data.messages.length > 0 && (
          <Section title="Bureau Messages" count={data.messages.length}>
            <Stack spacing={0.5}>
              {data.messages.map((m, i) => (
                <Typography key={i} sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, lineHeight: 1.45 }}>
                  {m.code && (
                    <Box component="span" sx={{ fontFamily: MONO_FONT, fontWeight: 700, color: alpha(theme.palette.text.primary, 0.65), mr: 0.625 }}>
                      [{m.code}]
                    </Box>
                  )}
                  {m.text}
                </Typography>
              ))}
            </Stack>
          </Section>
        )}
      </Box>
    </Box>
  );
};

export default ReportCard;
